/**
 * savePicker — the pure, testable display logic for the Continue save-picker (D16:
 * multi-slot saves). Turns a {@link SaveSummary} into the human-readable strings the
 * TitleScene renders: a warm relative "last-played" stamp ("today 8:14pm", "Jun 19")
 * and a glanceable progress summary ("Lv 14 · 23 caught"). No Phaser, no IO — so the
 * formatting is unit-tested independently of the scene, the same way menuNav/scroll
 * keep the widgets' math testable.
 */
import type { SaveSummary } from '@/session';

/** A row's display strings, ready to drop onto the picker. */
export interface SaveRowLabels {
  /** The trainer's name (the row's headline). */
  readonly name: string;
  /** A warm relative last-played stamp ("today 8:14pm" / "yesterday" / "Jun 19"). */
  readonly when: string;
  /** A glanceable progress summary ("Lv 14 · 23 caught"). */
  readonly summary: string;
}

const MS_PER_DAY = 86_400_000;

/** Lowercase 12-hour clock like "8:14pm" / "12:03am" (the cozy, un-shouty form). */
function clockTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h < 12 ? 'am' : 'pm';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')}${ampm}`;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Midnight (local) of the day containing `ms` — so "today" is a calendar day, not 24h. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * A warm, relative last-played stamp. Same calendar day => "today 8:14pm"; the day
 * before => "yesterday"; within the past week => the weekday ("Tue"); otherwise a
 * short date ("Jun 19", or "Jun 19, 2025" when it isn't this year). Never a harsh
 * full timestamp — this is a cozy hearth, not a log viewer.
 *
 * `now` is injectable so the formatting is deterministic in tests.
 */
export function formatLastPlayed(lastPlayedAt: number, now: number = Date.now()): string {
  const then = new Date(lastPlayedAt);
  const dayDiff = Math.round((startOfDay(now) - startOfDay(lastPlayedAt)) / MS_PER_DAY);

  if (dayDiff <= 0) return `today ${clockTime(then)}`;
  if (dayDiff === 1) return 'yesterday';
  if (dayDiff < 7) {
    return then.toLocaleDateString(undefined, { weekday: 'short' });
  }

  const sameYear = then.getFullYear() === new Date(now).getFullYear();
  const base = `${MONTHS[then.getMonth()]} ${then.getDate()}`;
  return sameYear ? base : `${base}, ${then.getFullYear()}`;
}

/** Pluralize "caught" — "1 caught" / "0 caught" / "23 caught". */
function caughtPhrase(caught: number): string {
  const n = Math.max(0, Math.floor(caught));
  return `${n} caught`;
}

/** A glanceable progress summary: "Lv 14 · 23 caught". */
export function formatSummary(level: number, caught: number): string {
  const lv = Math.max(1, Math.floor(level));
  return `Lv ${lv} · ${caughtPhrase(caught)}`;
}

/** Build all three display strings for one picker row. */
export function saveRowLabels(save: SaveSummary, now: number = Date.now()): SaveRowLabels {
  const name = save.trainerName.trim().length > 0 ? save.trainerName.trim() : 'Wanderer';
  return {
    name,
    when: formatLastPlayed(save.lastPlayedAt, now),
    summary: formatSummary(save.level, save.caught),
  };
}

/** The single-line label used by the ▶-cursor Menu (name + when + summary). */
export function saveMenuLabel(save: SaveSummary, now: number = Date.now()): string {
  const { name, when, summary } = saveRowLabels(save, now);
  return `${name}  ·  ${when}  ·  ${summary}`;
}

/** The confirm-delete prompt for a save (gentle, irreversible-but-calm wording). */
export function deleteConfirmText(save: SaveSummary): string {
  const name = save.trainerName.trim().length > 0 ? save.trainerName.trim() : 'this save';
  return `Delete ${name}? This can't be undone.`;
}
